import { useEffect } from 'react'
import { cn } from '../../lib/utils'
import { Button } from './Button'

export function Modal({
  open,
  title,
  children,
  onClose,
  footer,
  size = 'md',
  contentClassName = '',
  dialogRole = 'dialog',
  /** Raise above another open modal (e.g. nested pickers). */
  elevated = false,
}) {
  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.()
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [open, onClose])

  if (!open) return null

  const maxW =
    size === 'sm'
      ? 'max-w-md'
      : size === 'lg'
        ? 'max-w-2xl'
        : size === 'xl'
          ? 'max-w-4xl'
          : 'max-w-lg'

  return (
    <div
      className={cn(
        'fixed inset-0 flex items-end justify-center p-4 sm:items-center sm:p-6',
        elevated ? 'z-[70]' : 'z-50',
      )}
    >
      <button
        type="button"
        aria-label="Close dialog"
        className="absolute inset-0 cursor-pointer bg-black/50 backdrop-blur-sm dark:bg-black/75"
        onClick={onClose}
      />
      <div
        role={dialogRole}
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
        className={cn(
          'relative z-10 flex max-h-[min(90vh,56rem)] w-full flex-col overflow-hidden rounded-2xl border border-border bg-background text-foreground shadow-[var(--shadow-modal)]',
          maxW,
        )}
      >
        <div className="flex shrink-0 items-center justify-between gap-4 border-b border-border px-6 py-5 sm:px-8">
          {title ? (
            <h2 id="modal-title" className="text-lg font-semibold tracking-tight text-foreground">
              {title}
            </h2>
          ) : (
            <span />
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="min-h-10 min-w-10 shrink-0 rounded-2xl !p-0 text-muted-foreground hover:text-foreground"
            onClick={onClose}
            aria-label="Close"
          >
            <span className="text-lg leading-none">×</span>
          </Button>
        </div>
        <div
          className={cn(
            'min-h-0 flex-1 overflow-y-auto px-6 py-5 text-sm leading-relaxed text-muted-foreground sm:px-8 [&_.bg-card]:bg-background',
            contentClassName,
          )}
        >
          {children}
        </div>
        {footer ? (
          <div className="flex shrink-0 flex-col-reverse gap-3 border-t border-border bg-background px-6 py-5 sm:flex-row sm:justify-end sm:px-8">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  )
}
